/** @jsx jsx */
import { jsx } from '@emotion/react';
import { Scanner, prepareZXingModule } from '@yudiel/react-qr-scanner';
import { Button } from '../button';
import { useState } from 'react';
import { Row } from '../row';
import { get } from 'lodash';
import zxingReaderWasm from 'zxing-wasm/reader/zxing_reader.wasm';

// The scanner decodes with a WebAssembly build of ZXing. Left alone it would
// pull that 1MB binary off a public CDN (fastly.jsdelivr.net) the first time
// anyone scans, which breaks on restricted networks and puts executable code
// outside our own bundle. Point it at the copy webpack emits instead, so the
// binary ships and is served with the rest of the app.
//
// zxing-wasm is deliberately NOT in package.json: the binary has to match the
// decoder glue byte for byte, and that glue is bundled inside barcode-detector
// (@yudiel/react-qr-scanner -> barcode-detector -> zxing-wasm, pinned there to
// an exact version). Importing the hoisted copy means we always emit the exact
// binary the installed glue expects. Declaring our own version could drift from
// it, which would fail at scan time rather than at build time.
prepareZXingModule({
  overrides: {
    locateFile: (path: string, prefix: string) =>
      path.endsWith('.wasm') ? zxingReaderWasm : prefix + path,
  },
});

export const QRScan = (props: any) => {
  const [state, setState] = useState({
    facingMode: 'environment',
    delay: 500,
  })

  return (
    <div style={{ width: '100%', height: '100%',  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '100%', height: '100%', maxWidth: 500, margin: '0 auto' }}>
        <Scanner
          constraints={{ facingMode: state.facingMode }}
          formats={['qr_code']}
          scanDelay={state.delay}
          onScan={(codes: any) => {
            const value = get(codes, '[0].rawValue');
            if (value) {
              props.onResult(null, value);
            }
          }}
          onError={(error: any) => {
            props.onResult(error);
          }}
          // Keep the bare video viewport this screen has always had: no finder
          // overlay, no torch button and no scan beep.
          components={{ finder: false, torch: false }}
          sound={false}
          styles={{ container: { width: '100%' }, video: { width: '100%' } }}
        />
        <Row center style={{ marginTop: 20 }}>
          <Button title={`Toggle Camera`} onClick={() => setState(s =>({ ...s, facingMode: s.facingMode === 'user' ? 'environment' : 'user' }))} />
        </Row>
      </div>
    </div>
  )
}

// `modalApi` must be the instance API from antd's App context — App.useApp().modal,
// which useAppConfig() also re-exports. The static Modal.* helpers mount their
// dialog in a React root of their own, outside <Provider> and <ConfigProvider>:
// the content then dies with "could not find react-redux context value" the
// moment a Button calls useAppConfig(), and the footer renders in antd's default
// blue because it never sees our theme. Going through App.useApp() renders the
// dialog inside the app tree, so both come for free.
export const ScanQRCode = (modalApi: any, props = {}) => {
  return new Promise((resolve, reject) => {
    const getWidth = () => {
      if (get(props, 'isPhone')) {
        return '100%';
      } else {
        const w = document.body.clientWidth;
        return w > 700 ? 600 : w;
      }
    }

    const modal = modalApi.info({
      title: (
        <Row>
          <h3>Scan QR Code</h3>
          <Button outline style={{ position: 'absolute', top: 10, right: 5, borderColor: 'rgba(0,0,0,0.0)' }} icon="close" title="" onClick={() => {
            modal.destroy();
            reject();
          }} />
        </Row>
      ),
      icon: null,
      maskClosable: true,
      okText: 'Close',
      content: (
        <QRScan {...props} onResult={(err: any, res: any) => {
          modal.destroy();
          if (err) {
            return reject(err)
          }
          resolve(res)
        }} />
      ),
      width: getWidth(),
      onCancel: () => {
        reject();
      },
      onOk: () => {
        reject();
      },
      ...props,
    })
  })
}
